import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { IApiQueryOptions } from 'api/ApiClient';
import { POS_CATEGORIES } from 'config/const';
import { useFeatureFlags } from 'contexts/featureFlags';
import routeConstants from 'routes/routeConstants';
import { clearAllRequests, selectAPIRequestStatus } from 'store/ApiClient';
import { selectPaymentSetsForSite } from 'store/Collections';
import { EmployeePayrollTips, getPayPeriodPayrollTipsRequest, selectEmployeePayrollTips } from 'store/Collections/EmployeePayrollTips';
import { Integrations, listSiteIntegrationsRequest, selectSiteIntegrations } from 'store/Collections/Integrations';
import { getPaymentRequest } from 'store/Payment';
import { getCalculatedTipsRequest, getPOSDataRequest, selectCalculatorPOSResults, setCalculatorPayees, TipCalculator, updateCalculatorPaymentset } from 'store/TipCalculator';
import { IStore } from 'store/types';
import {
  ApiQueryOperator,
  CalculatorSettingsChoice,
  FeatureFlags,
  ICalculatorPaymentSet,
  ICalculatorRules,
  IDaySegment,
  IntegrationStatus,
  IPaymentSet,
  IPaymentSetCalculations,
  IPayPeriod,
  ISite,
  ITipCalculatorErrorPayload,
  ITipCalculatorResult,
  RequestStatus,
  SettingsGateState,
} from 'types';
import { siteUsesWithholdings } from 'utils';
import { buildCurrentPOSIntegrations, buildReplayInputs, buildSettingsOverride, compareCalculatorSettings, logSettingsComparison, resolveSettingsGateState } from 'utils/calculatorSettings';
import {
  buildTipCalculatorPayeeFromPOSData,
  buildTipCalculatorRequests,
  PosUnclosedChecksError,
  processTipCalculatorError,
  TipCalculationNoLaborForTicketsError,
  TipCalculationValidationError,
  TipCalculationValidationType,
  validateTipRulesAgainstLaborData,
} from 'utils/tipCalculator';
import { IContentProps } from '../types';

/** The integrations the snapshot's settings block covered — the same narrowing `getPosData` applies. */
const SNAPSHOT_INTEGRATIONS_QUERY: IApiQueryOptions = {
  q: [
    { key: 'status', operator: ApiQueryOperator.eq, value: IntegrationStatus.ACTIVE },
    { key: 'categories', operator: ApiQueryOperator.in, value: POS_CATEGORIES },
  ],
};

/** A gate put to the manager. Carries the snapshot, so a replay stays choosable if the gate has to be put again. */
interface IAwaitingGate {
  state: SettingsGateState;
  snapshot?: IPaymentSetCalculations;
}

/** What the manager answered: which gate they were shown, and the settings they picked on it. */
interface ISettingsAnswer {
  gate?: IAwaitingGate;
  choice?: CalculatorSettingsChoice;
}

/**  How far the settings gate has got: only `cleared` releases the calculation. */
type GateStage =
  | { stage: 'fetchingPayment' }
  /** `paymentFetched` says the payment request finished — with a payment, or without one when it failed. */
  | { stage: 'paymentFetched'; payment?: IPaymentSet }
  | ({ stage: 'awaitingChoice' } & IAwaitingGate)
  /*
   * Holds the answer, which says both which settings the loads run under and which question a failed load can put
   * back. Empty on an unpaid date, which clears without ever asking.
   */
  | ({ stage: 'cleared' } & ISettingsAnswer);

/**
 * The stored settings an answer runs on: only Original replays the snapshot the payment was calculated under. Every
 * other answer — Current, or an informational gate with nothing to choose — means the site's live settings.
 */
const replayedSnapshot = (answer?: ISettingsAnswer) => (answer?.choice === CalculatorSettingsChoice.ORIGINAL ? answer.gate?.snapshot : undefined);

/** The states the processing modal can show. */
type ContentKey = 'loadData' | 'loadPOSError' | 'loadPayrollError' | 'noLaborForTicketsError' | 'unclosedChecksError' | 'calculating' | 'calculateSuccess' | 'calculateError';

/** What the manager is shown for a rule-validation error — not a settings gate. Leaving `buttonText`/`onClick` out
 * gives a Retry that reloads POS data; set them where a reload would change nothing. */
interface IValidationPrompt {
  message: string;
  buttonText?: string;
  onClick?(): void;
}

interface IUseInitialWorkflowArgs {
  site: ISite;
  businessDate: string;
  tipCalcRules: ICalculatorRules;
  segments: IDaySegment[];
  paymentSet: ICalculatorPaymentSet;
  onClose(): void;
}

export const useInitialWorkflow = ({ site, businessDate, tipCalcRules, segments, paymentSet, onClose }: IUseInitialWorkflowArgs) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const posStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, TipCalculator.GET_POS_REQUEST));
  const payrollTipsStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, EmployeePayrollTips.GET_PAY_PERIOD_PAYROLL_TIPS_REQUEST));
  const payrollTips = useSelector(selectEmployeePayrollTips());
  const currentPOSResults = useSelector(selectCalculatorPOSResults());
  const withholdingEnabled = siteUsesWithholdings(site);
  const { timezone, tipCalculatorPercentFee: creditCardPercentFee } = site?.settings || {};

  const settingsGateEnabled = useFeatureFlags()[FeatureFlags.CALCULATOR_ORIGINAL_SETTINGS]?.enabled ?? false;
  const paymentSetsForSite = useSelector(selectPaymentSetsForSite());
  // Read from the already-loaded collection: `DateSelector` gates the calendar on it, so paid dates are known.
  const existingPayment = settingsGateEnabled ? paymentSetsForSite?.find((payment) => payment.paymentDate === businessDate) : undefined;

  // The one comparison input not already in props. Taking it from the integrations endpoint rather than a POS
  // response is what lets the gate be answered before any POS request is made.
  const siteIntegrations = useSelector(selectSiteIntegrations(site.id));
  const siteIntegrationsStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, Integrations.LIST_SITE_INTEGRATIONS_REQUEST));

  const [gate, setGate] = useState<GateStage>(existingPayment ? { stage: 'fetchingPayment' } : { stage: 'cleared' });

  const contentPropsMap: Record<ContentKey, Partial<IContentProps>> = {
    loadData: {
      message: 'Hang tight, this may take a moment.',
      stepMessage: 'Retrieving Data...',
      status: 'loading',
      buttonText: 'Review Payouts',
    },
    loadPayrollError: {
      message: 'Something went wrong',
      stepMessage: 'We are having trouble retrieving your payroll data.',
      status: 'error',
      buttonText: 'Reload Data',
      onClick: () => loadPayrollData(),
    },
    loadPOSError: {
      message: 'Something went wrong',
      stepMessage: 'We are having trouble retrieving your data.',
      status: 'error',
      buttonText: 'Reload Data',
      onClick: () => loadPOSData(),
    },
    noLaborForTicketsError: {
      message: 'The following items need your attention before proceeding.',
      status: 'validationError',
      buttonText: 'Retry',
      onClick: (skipNoLabor?: boolean) => loadPOSData(skipNoLabor),
    },
    unclosedChecksError: {
      message: 'The issue below must be resolved before proceeding.',
      status: 'validationError',
      buttonText: 'Retry',
      onClick: () => loadPOSData(),
    },
    calculating: {
      message: 'Hang tight, this may take a moment.',
      stepMessage: 'Calculating tips...',
      status: 'loading',
      buttonText: 'Review Payouts',
    },
    calculateSuccess: {
      message: 'All systems go!',
      stepMessage: 'Success!',
      status: 'success',
      buttonText: 'Review Payouts',
      onClick: () => {
        history.push(routeConstants.private.makePaymentCalculatorReview);
        onClose();
      },
    },
    calculateError: {
      stepMessage: 'An error has occurred calculating tips',
      status: 'error',
      buttonText: 'Try Again',
      onClick: () => loadPOSData(),
    },
  };

  const [contentProps, setContentProps] = useState<Partial<IContentProps>>(contentPropsMap.loadData);

  const clearedGate = gate.stage === 'cleared' ? gate : undefined;
  // Feeds the two things that consume the answer: the settings each POS load posts, and the rules and segment
  // windows the calculation runs under.
  const replay = replayedSnapshot(clearedGate);

  const onPOSDataFailure = (errors: any, answeredGate?: IAwaitingGate) => {
    const errorsArray = Array.isArray(errors) ? errors : [];
    const codes = new Set(errorsArray.map((error: any) => error.code));

    /*
     * However the load failed, the settings it ran under could be the reason, so the gate is offered back — but only
     * a CHANGED one, the single state that put an option there to take.
     *
     * The first load hands its gate in, since the handler that clears it closes over the stage from before; later
     * loads read it off that stage.
     */
    const returnTo = answeredGate || clearedGate?.gate;
    const backToGate = returnTo?.state === SettingsGateState.CHANGED && { onSecondaryClick: () => setGate({ stage: 'awaitingChoice', ...returnTo }) };

    if (codes.has('pos_unclosed_checks')) setContentProps({ ...contentPropsMap.unclosedChecksError, ...backToGate, errors: new PosUnclosedChecksError(errorsArray) });
    else if (codes.has('pos_no_labor_for_ticket')) setContentProps({ ...contentPropsMap.noLaborForTicketsError, ...backToGate, errors: new TipCalculationNoLaborForTicketsError('No labor for tickets', errorsArray) });
    else setContentProps({ ...contentPropsMap.loadPOSError, ...backToGate, errors });
  };

  // The answer defaults to the gate's own, so retry buttons keep the manager's choice without knowing about it.
  const loadPOSData = (skipNoLabor?: boolean, answer: ISettingsAnswer | undefined = clearedGate) => {
    const onFailure = (errors: any) => onPOSDataFailure(errors, answer?.gate);
    dispatch(getPOSDataRequest(site.id, businessDate, undefined, onFailure, skipNoLabor, buildSettingsOverride(replayedSnapshot(answer))));
    setContentProps(contentPropsMap.loadData);
  };

  const loadPayrollData = () => {
    const onSuccess = (payPeriod: IPayPeriod) => dispatch(updateCalculatorPaymentset({ ...paymentSet, payPeriod }));
    dispatch(getPayPeriodPayrollTipsRequest(site.id, businessDate, paymentSet.payPeriod, onSuccess));
    setContentProps(contentPropsMap.loadData);
  };

  /** Both loads together. Neither feeds the gate, so a paid date waits for the answer and a backed-out one pays for neither. */
  const loadCalculationData = (answer?: ISettingsAnswer) => {
    loadPOSData(undefined, answer);
    if (withholdingEnabled) loadPayrollData();
  };

  /** The gate's own inputs, neither reaching the POS provider. A payment that fails to fetch advances the same way:
   * nothing to compare is informational, not blocking. */
  const loadGateData = (paymentId: string) => {
    const onSettled = (payment?: IPaymentSet) => setGate({ stage: 'paymentFetched', payment });
    dispatch(getPaymentRequest(paymentId, false, onSettled, onSettled));
    dispatch(listSiteIntegrationsRequest(site.id, SNAPSHOT_INTEGRATIONS_QUERY));
  };

  const onTipCalcSuccess = (tipCalcResults: ITipCalculatorResult[]) => {
    try {
      dispatch(updateCalculatorPaymentset(paymentSet));

      const payees = buildTipCalculatorPayeeFromPOSData(tipCalcResults, currentPOSResults, site, payrollTips?.data);
      dispatch(setCalculatorPayees(payees));

      setContentProps(contentPropsMap.calculateSuccess);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('error building tip calculator payee from labor ticket data', err);
      setContentProps({ ...contentPropsMap.calculateError, errors: [err] });
    }
  };

  const onTipCalcFailure = (errorPayload: ITipCalculatorErrorPayload) => {
    const processedError = processTipCalculatorError(errorPayload);
    setContentProps({ ...contentPropsMap.calculateError, errors: processedError });
  };

  const calculateTips = (skipValidation = false) => {
    try {
      // A replay calculates from the snapshot; every other path keeps the site's live rules and segments.
      const replayInputs = buildReplayInputs(replay, currentPOSResults);
      const calculationRules = replayInputs?.rules || tipCalcRules;

      const tipCalcRequests = buildTipCalculatorRequests(currentPOSResults, calculationRules, replayInputs?.daySegments || segments);
      if (!skipValidation) validateTipRulesAgainstLaborData(currentPOSResults, calculationRules, tipCalcRequests);
      dispatch(getCalculatedTipsRequest(tipCalcRequests, currentPOSResults, creditCardPercentFee, onTipCalcSuccess, onTipCalcFailure));
      setContentProps(contentPropsMap.calculating);
    } catch (error: any) {
      if (error instanceof TipCalculationValidationError) {
        onTipRuleValidationError(error);
      } else {
        onPOSDataFailure(error);
      }
    }
  };

  /** Highest priority first: the first type present in the error wins. */
  const validationPrompts: Array<IValidationPrompt & { type: TipCalculationValidationType }> = [
    { type: TipCalculationValidationType.NO_TIPS_OR_AUTO_GRATUITIES_TO_DISTRIBUTE, message: 'The issue below must be resolved before proceeding.', buttonText: 'Close', onClick: onClose },
    { type: TipCalculationValidationType.EMPLOYEES_INVALID_TIME_CLOCK_ENTRIES, message: 'The issue below must be resolved before proceeding.' },
    { type: TipCalculationValidationType.EMPLOYEES_STILL_CLOCKED_IN, message: 'The warning below must be resolved before proceeding.' },
    { type: TipCalculationValidationType.EMPLOYEES_NO_BREAK_END, message: 'The warning below must be resolved before proceeding.' },
    {
      type: TipCalculationValidationType.NO_ELIGIBLE_EMPLOYEES,
      message: 'Based on your rules, there are no employees who worked matching your rules. Please check your configuration.',
      buttonText: 'Go to Settings',
      onClick: () => history.push(`${routeConstants.private.settingsCalculator}/tip-shares`),
    },
    { type: TipCalculationValidationType.NO_TIP_SHARE_RULE_FOR_JOB, message: 'The issues below must be resolved before proceeding.' },
  ];

  /** No matching type: the warnings are advisory, so calculating anyway is allowed. */
  const unmatchedValidationPrompt: IValidationPrompt = { message: 'Please review the warnings below before proceeding.', buttonText: 'Calculate Payments', onClick: () => calculateTips(true) };

  const onTipRuleValidationError = (validationError: TipCalculationValidationError) => {
    const errorTypes = new Set(validationError.errors.map((e) => e.type));
    const { message, buttonText = 'Retry', onClick = () => loadPOSData() } = validationPrompts.find(({ type }) => errorTypes.has(type)) ?? unmatchedValidationPrompt;

    setContentProps({ message, buttonText, onClick, status: 'validationError', errors: validationError });
  };

  /**
   * Compares the payment's stored settings against the site's current ones, and puts the gate up on the answer.
   *
   * Either side missing shows the informational state rather than differences it cannot stand behind — losing the
   * comparison is not a reason to block a payout.
   */
  const resolveSettingsGate = (payment?: IPaymentSet) => {
    const showGate = (state: SettingsGateState) => setGate({ stage: 'awaitingChoice', state, snapshot: payment?.calculations });

    if (!payment?.calculations || siteIntegrationsStatus === RequestStatus.FAILED) {
      showGate(SettingsGateState.INCOMPLETE);
      return;
    }

    const currentSettings = {
      siteSettings: site.settings,
      calculatorRules: tipCalcRules,
      // Already filtered to this date's weekday upstream; the comparison re-filters, a no-op here.
      daySegments: segments,
      integrations: buildCurrentPOSIntegrations(siteIntegrations),
    };
    const comparison = compareCalculatorSettings(payment.calculations, currentSettings, businessDate);

    logSettingsComparison(comparison, { paymentId: payment.id, date: businessDate });
    showGate(resolveSettingsGateState(comparison));
  };

  /** Clears the gate on the manager's answer, and starts the loads that answer selected. */
  const onSettingsGateCalculate = (answeredGate: IAwaitingGate, choice?: CalculatorSettingsChoice) => {
    const answer: ISettingsAnswer = { gate: answeredGate, choice };
    setGate({ stage: 'cleared', ...answer });
    // Hand answer directly rather than read back off the stage above, which is not applied on this render yet.
    loadCalculationData(answer);
  };

  /**
   * Starts the workflow A date with no payment loads what the calculation needs and continues.
   * A paid date loads the gate's inputs and waits for the manager to pick which settings to use for retrieving POS data.
   */
  useEffect(() => {
    if (existingPayment) loadGateData(existingPayment.id);
    else loadCalculationData();
  }, [site.id]); // `existingPayment` is left out on purpose, uses first-render value.

  /** Raises the gate once both of its inputs have settled. POS request is not one of them. */
  useEffect(() => {
    if (gate.stage === 'paymentFetched' && (siteIntegrationsStatus === RequestStatus.SUCCESS || siteIntegrationsStatus === RequestStatus.FAILED)) {
      resolveSettingsGate(gate.payment);
    }
  }, [gate, siteIntegrationsStatus]);

  /**
   * Calculates once the manager has answered and the data that answer selected has landed. Retry buttons re-enter
   * here on an already-cleared gate, which is why a retry recalculates rather than asking again.
   */
  useEffect(() => {
    // POS failures surface through the load's own callback, so nothing here acts until a retrieval succeeds.
    if (gate.stage !== 'cleared' || posStatus !== RequestStatus.SUCCESS) return;

    // Payroll is the only load without a failure callback, so its state is set here. A POS error has priority.
    if (withholdingEnabled && payrollTipsStatus !== RequestStatus.SUCCESS) {
      if (payrollTipsStatus === RequestStatus.FAILED) setContentProps(contentPropsMap.loadPayrollError);
      return;
    }

    calculateTips();
  }, [gate, posStatus, payrollTipsStatus]);

  useEffect(() => () => dispatch(clearAllRequests()), []);

  return {
    contentProps,
    timeZone: timezone,
    settingsGate: gate.stage === 'awaitingChoice' ? { state: gate.state, onCancel: onClose, onCalculate: (choice?: CalculatorSettingsChoice) => onSettingsGateCalculate(gate, choice) } : undefined,
  };
};
